import type { TFunction } from 'i18next';
import type { FirestoreError } from 'firebase/firestore';

/**
 * Translates an onSnapshot listener failure into a message a member can act on.
 *
 * WHY THIS EXISTS: every `onSnapshot(q, next)` written without the second, error
 * callback DISCARDS its failure. The belt-promotion feature broke silently twice
 * because of it — first a missing security rule, then a missing composite index —
 * and both times the widget rendered a confident "0 pending", which reads as
 * "nobody asked" rather than "this is broken". No console output, no toast, no
 * error state: the UI cannot distinguish an empty collection from a denied query.
 *
 * Pass this to the error callback of any listener whose result the user sees.
 */

const CODE_KEYS: Record<string, string> = {
    // The rule denied the query (or the collection has no rule at all).
    'permission-denied': 'errors.listener.permission_denied',
    // A composite index the query needs does not exist — the belt-widget outage.
    'failed-precondition': 'errors.listener.index_missing',
    unauthenticated: 'errors.listener.unauthenticated',
    unavailable: 'errors.listener.offline',
    'deadline-exceeded': 'errors.listener.offline',
    'resource-exhausted': 'errors.listener.try_again_later',
    cancelled: 'errors.listener.try_again_later',
};

export function listenerErrorMessage(t: TFunction, err: unknown): string {
    const code = (err as FirestoreError | undefined)?.code;
    const key = code ? CODE_KEYS[code] : undefined;
    return key ? t(key) : t('errors.listener.generic');
}

/**
 * One place that logs a listener failure with enough context to debug it.
 *
 * `failed-precondition` carries the console URL that creates the missing index, so
 * the raw error is logged too rather than only a friendly summary.
 */
/**
 * The same mapping for a WRITE that failed, not a listener.
 *
 * Added after the 2026-09-02 P0: onboarding's SubscriptionStep caught every failure
 * into `showError("Failed to select plans. Please try again.")`. The real error was
 * a rules denial, so "try again" could never work — the member was told to repeat an
 * action that was guaranteed to fail, and we got no diagnostic. Two people were
 * turned away before anyone noticed.
 */
export function writeErrorMessage(t: TFunction, err: unknown): string {
    const code = (err as FirestoreError | undefined)?.code;
    // 'permission-denied' on a write is not the member's fault and not retryable;
    // say so, and point them at a human.
    if (code === 'permission-denied') return t('errors.write.permission_denied');
    return listenerErrorMessage(t, err);
}

/**
 * The message to show a member for ANY failed action.
 *
 * Distinguishes two things that look alike in a catch block:
 *   - a Firestore failure (carries a `code` like 'permission-denied') — internal,
 *     must never be shown raw. "Missing or insufficient permissions" reached a
 *     member's phone on 2026-09-04 because `error.message` went straight to a toast.
 *   - an error the app threw on purpose (no `code`) — e.g. "Prerequisites not met:
 *     Minimum belt rank: Blue". That text is FOR the member and must survive.
 */
export function memberFacingError(t: TFunction, err: unknown, fallbackKey = 'errors.listener.generic'): string {
    const code = (err as FirestoreError | undefined)?.code;
    if (typeof code === 'string' && code.includes('/')) {
        // Firebase codes arrive as 'firestore/permission-denied' in some paths.
        return writeErrorMessage(t, { ...(err as object), code: code.split('/').pop() } as FirestoreError);
    }
    if (code) return writeErrorMessage(t, err);
    const message = err instanceof Error ? err.message : '';
    return message || t(fallbackKey);
}

/** True when the failure is a permission denial the member cannot resolve alone. */
export function isPermissionDenied(err: unknown): boolean {
    const code = (err as FirestoreError | undefined)?.code ?? '';
    return typeof code === 'string' && code.endsWith('permission-denied');
}

/**
 * Log a failed write with enough context to diagnose it without a repro:
 * which operation, which collection, and whose uid.
 */
export function logWriteError(
    ctx: { operation: string; collection: string; uid?: string },
    err: unknown,
): void {
    const code = (err as FirestoreError | undefined)?.code ?? 'unknown';
    console.error(
        `[firestore-write] ${ctx.operation} on ${ctx.collection} failed (${code})`
        + ` uid=${ctx.uid ?? 'anonymous'}:`, err,
    );
}

export function logListenerError(label: string, err: unknown): void {
    const code = (err as FirestoreError | undefined)?.code ?? 'unknown';
    console.error(`[${label}] Firestore listener failed (${code}):`, err);
}
